/**
 * @fileoverview Toggle - Toggle button component
 *
 * Purpose: Two-state toggle button (on/off)
 * Dependencies: Radix UI Toggle primitive, CVA
 * Used by: Toolbars, formatting controls, settings
 *
 * Features:
 * - Pressed/unpressed states
 * - Multiple variants (default, outline)
 * - Multiple sizes (sm, default, lg)
 * - Keyboard accessible
 * - Icon support
 *
 * @example
 * <Toggle aria-label="Toggle italic">
 *   <Italic className="h-4 w-4" />
 * </Toggle>
 *
 * @example
 * // Controlled
 * <Toggle pressed={isBold} onPressedChange={setIsBold}>
 *   <Bold className="h-4 w-4" />
 * </Toggle>
 */

"use client";

import * as React from "react";
import * as TogglePrimitive from "@radix-ui/react-toggle";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/misc";
import { toggleVariants } from "./toggle.variants";

const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(toggleVariants({ variant, size, className }))}
    {...props}
  />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

export { Toggle };
